import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lock, Loader2, Plus, Trash2, Image as ImageIcon, User, UserCog } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RsvpPrivateInspoViewer({ rsvpId, eventId, category }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  const reload = useCallback(async () => {
    if (!rsvpId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/rsvp/${rsvpId}/private-inspo`, { headers: headers() });
      if (r.ok) { const d = await r.json(); setItems(d.inspo || []); }
    } catch {} finally { setLoading(false); }
  }, [rsvpId]);

  useEffect(() => { reload(); }, [reload]);

  const upload = async (file) => {
    if (!file || !eventId) return;
    setUploading(true);
    try {
      // First upload the image (reuse client-photos endpoint)
      const fd = new FormData();
      fd.append("file", file);
      fd.append("rsvp_id", rsvpId);
      const upR = await fetch(`${API}/host/events/${eventId}/client-photos`, { method: "POST", headers: headers(), body: fd });
      if (!upR.ok) { toast.error("Upload failed"); return; }
      const upD = await upR.json();
      // Then create rsvp_private_inspo with uploader_role host
      const r = await fetch(`${API}/rsvp/${rsvpId}/private-inspo`, {
        method: "POST",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({ image_url: upD.image_url, uploader_role: "host", notes: "" })
      });
      if (r.ok) { toast.success("Private inspo added for this client"); reload(); }
      else toast.error("Save failed");
    } catch { toast.error("Network error"); } finally { setUploading(false); }
  };

  const removeInspo = async (inspoId) => {
    try {
      const r = await fetch(`${API}/rsvp/${rsvpId}/private-inspo/${inspoId}`, { method: "DELETE", headers: headers() });
      if (r.ok) { toast.success("Removed"); reload(); }
    } catch {}
  };

  return (
    <div className="border-t mx-4 mt-3 pt-3 pb-4" data-testid="rsvp-private-inspo-viewer">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
          <Lock className="w-3 h-3 text-purple-600" /> Private inspo for this client
          {items.length > 0 && <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>}
        </div>
        <label className="text-[11px] text-purple-700 cursor-pointer inline-flex items-center gap-1 hover:bg-purple-50 px-2 py-0.5 rounded" data-testid="rsvp-inspo-upload-label">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
          <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ""; }} />
        </label>
      </div>

      {loading ? <Loader2 className="w-4 h-4 animate-spin text-purple-500 mx-auto" /> : items.length === 0 ? (
        <p className="text-[11px] text-gray-400 italic">No private inspo yet. Upload your own picks for this client, or wait for client uploads on confirmation page.</p>
      ) : (
        <div className="grid grid-cols-3 gap-1.5">
          {items.map((it) => (
            <div key={it.inspo_id} className="relative group rounded overflow-hidden border bg-gray-50 aspect-square" data-testid={`rsvp-inspo-${it.inspo_id}`}>
              <img src={it.image_url} alt="" className="w-full h-full object-cover" />
              <div className="absolute top-0.5 left-0.5">
                {it.uploader_role === "host" ? (
                  <Badge className="text-[8px] bg-purple-600 text-white"><UserCog className="w-2 h-2 mr-0.5" /> You</Badge>
                ) : (
                  <Badge className="text-[8px] bg-emerald-600 text-white"><User className="w-2 h-2 mr-0.5" /> Client</Badge>
                )}
              </div>
              <button
                onClick={() => removeInspo(it.inspo_id)}
                className="absolute top-0.5 right-0.5 bg-red-500 text-white p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`rsvp-inspo-delete-${it.inspo_id}`}
              >
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
